/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import Table from '../../components/table/Table';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import Avatar from '../../components/avatar';
import LoadingPlaceholder from '../../components/LoaderContainer/LoadingPlaceholder';
import { Portfolio } from '../../types/portfolio';
import { DATE, formatDate } from '../../utils/date';
import { naturalSort } from '../../utils';
import { MetricGroup, MetricGroupShare } from '../../types/metricGroup';
import { removeCompanyShareFromMetricGroup } from '../../actions/initiative';
import Loader from '../../components/loader';
import G17Client from '../../services/G17Client';
import './styles.scss';
import { useSiteAlert } from '../../hooks/useSiteAlert';

interface MenuButtonProps {
  portfolioId: string;
  groupId: string;
  companyId: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  handleReload: (resetView?: boolean) => void;
}

const MenuButton = ({ portfolioId, isReadOnly, groupId, companyId, disabled, handleReload }: MenuButtonProps) => {
  const [isSaving, setSaving] = React.useState(false);
  const { addSiteAlert } = useSiteAlert();

  const handleRemove = async (companyId: string) => {
    setSaving(true);
    try {
      await removeCompanyShareFromMetricGroup(portfolioId, groupId, companyId);
      addSiteAlert({ content: 'The pack has been removed' });
      handleReload();
    }
    catch (e) {
      console.log(e);
    }
    setSaving(false);
  }

  if (isSaving) {
    return <Loader />;
  }

  const handleDownloadIndividualData = async () => {
    const url = G17Client.getDownloadUrl(`portfolios/${portfolioId}/metrics/${groupId}/download`);
    window.open(url, '_blank', '');
  }

  return (
    <UncontrolledDropdown>
      <DropdownToggle color='transparent' outline className='px-2'>
        <i className='fas fa-bars' />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem disabled={disabled} size='sm' onClick={() => handleDownloadIndividualData()}>
          <i className='fa fa-file-csv mr-2' />Download company report for this metric group
          </DropdownItem>
        {!isReadOnly &&
          <>
            <DropdownItem divider />
            <DropdownItem disabled={disabled} size='sm' onClick={() => handleRemove(companyId)}>
              <i className='fa fa-times mr-2 text-ThemeDangerMedium' />Remove this company
            </DropdownItem>
          </>
        }
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

interface CompanyAssignmentsTableProps {
  metricGroup: MetricGroup;
  handleReload: (resetView?: boolean) => void;
  portfolio: Portfolio;
  isReadOnly?: boolean;
}

const CompanyAssignmentsTable = (props: CompanyAssignmentsTableProps) => {
  const { metricGroup, isReadOnly, handleReload, portfolio } = props;

  const [companies, setCompanies] = React.useState<MetricGroupShare[]>([]);
  const [isLoaded, setLoaded] = React.useState(false);

  const filteredCompanies = companies.filter(c => !!c.initiativeId);

  React.useEffect(() => {
    const companies = (metricGroup.share ? [...metricGroup.share] : [])
      .sort(
        (a, b) => naturalSort(a.initiative?.name ?? '', b.initiative?.name ?? '')
      );

    setCompanies(companies);
    setLoaded(true);
  }, [metricGroup]);

  if (!metricGroup._id) {
    // Only can do assignments once the metric group exists (no _id means it's not created yet)
    return <></>;
  }

  const tableColumns: any[] = [
    {
      accessor: 'initiativeName',
      Header: () => <span className='ml-4 pl-3'>Company Name</span>,
      cellProps: {
        className: 'nameCol'
      },
      Cell: (c: any) => {
        const r: MetricGroupShare = c.row.original;
        const company = r.initiative;
        if (!company) {
          console.log('Invalid company: ', r);
          return <></>;
        }

        return (
          <div className={'d-flex align-items-center'}>
            <div className='profile mr-2'>
              <Avatar width='30px'>
                {company.profile ? <img alt='avatar' src={company.profile} /> : <></>}
              </Avatar>
            </div>
            <div className='flex-fill'>
              {company.name ?? ''}
            </div>
          </div>
        );
      },
    },
    {
      accessor: 'acceptedDate',
      Header: () => 'Accepted Date',
      Cell: (c: any) => c.value ? formatDate(c.value, DATE.HUMANIZE) : 'not accepted'
    },
    {
      accessor: 'initiativeId',
      Header: () => '',
      cellProps: {
        className: 'buttonsCol'
      },
      Cell: (c: any) => (
        <MenuButton isReadOnly={isReadOnly} portfolioId={portfolio._id} groupId={metricGroup._id ?? ''} companyId={c.value} handleReload={handleReload} />
      )
    }
  ];

  const noResults = filteredCompanies.length === 0;

  return (
    <>
      <LoadingPlaceholder isLoading={!isLoaded} height={40} />
      <LoadingPlaceholder isLoading={!isLoaded} height={40} count={5} className='mt-1 background-ThemeTextWhite'>
        {noResults
          ?
          <div className='alert alert-primary'>This custom metric group has not been shared with any companies</div>
          :
          <div className='portfolio-table-container'>
            <Table responsive={true} showRowCount={true} columns={tableColumns} data={filteredCompanies} pageSize={25} />
          </div>
        }
      </LoadingPlaceholder>
    </>
  )
}

export default CompanyAssignmentsTable;
